import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Building2, MapPin, TrendingUp } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { caseStudies } from '@/data/caseStudies';

const CaseStudies = () => {
  const studies = caseStudies;

  return (
    <>
      <Helmet>
        <title>Commercial EPC &amp; MEES Case Studies | South West &amp; London | Energy Performance Direct</title>
        <meta name="description" content="Real Commercial EPC and MEES case studies from Energy Performance Direct. Period offices, industrial workshops, mixed-use and London unit splits — every project shows the rating journey, costs and payback." />
        <meta name="keywords" content="Commercial EPC case study, MEES case study, NDEPC case study, SBEM case study, EPC G to A, EPC G to B, MEES improvement plan, commercial energy advice case study" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-studies" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Commercial EPC & MEES Case Studies',
            description: 'Real Commercial EPC and MEES case studies from Energy Performance Direct.',
            url: 'https://energyperformancedirect.co.uk/case-studies',
            hasPart: studies.map((s) => ({
              '@type': 'Article',
              headline: s.headline,
              url: `https://energyperformancedirect.co.uk${s.route}`,
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Real projects · Real ratings · Real numbers</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                Commercial EPC &amp; MEES Case Studies
              </h1>
              <p className="text-lg text-gray-600 mb-2">
                Every project below is a real building we&rsquo;ve assessed and modelled — period offices, industrial workshops, mixed-use premises and London commercial splits. We show the starting rating, the staged improvement plan, the cost-and-payback maths and the rating we hit.
              </p>
            </div>
          </div>
        </section>

        {/* Case study cards */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
              {studies.map((s, idx) => (
                <Card key={idx} className="border-none shadow-lg hover:shadow-2xl transition-all overflow-hidden flex flex-col" data-testid={`case-study-card-${idx}`}>
                  <div className="bg-gradient-to-r from-green-600 to-cyan-500 p-6 text-white">
                    <div className="text-xs uppercase tracking-wider opacity-90 mb-2">{s.label}</div>
                    <h2 className="text-xl font-bold leading-tight">{s.headline}</h2>
                  </div>
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="grid grid-cols-3 gap-3 mb-5 text-center">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Area</div>
                        <div className="font-semibold text-gray-900 text-sm flex items-center justify-center gap-1">
                          <Building2 className="w-3 h-3 text-green-600" /> {s.area}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</div>
                        <div className="font-semibold text-gray-900 text-sm flex items-center justify-center gap-1">
                          <MapPin className="w-3 h-3 text-green-600" /> {s.location}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rating</div>
                        <div className="font-semibold text-gray-900 text-sm flex items-center justify-center gap-1">
                          <TrendingUp className="w-3 h-3 text-green-600" /> {s.ratingFrom} → {s.ratingTo}
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 text-sm mb-5 flex-1">{s.summary}</p>

                    <div className="flex flex-wrap gap-2 mb-5">
                      {s.tags.map((t, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full font-medium">{t}</span>
                      ))}
                    </div>

                    <Link to={s.route} className="self-start" data-testid={`case-study-link-${idx}`}>
                      <Button className="bg-gray-900 text-white hover:bg-gray-800 rounded-full">
                        Read full case study <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service cross-link */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                Want this kind of analysis on your own building?
              </h2>
              <p className="text-gray-700 mb-6">
                Every case study above started with a Commercial EPC and a MEES Advice Report. Both services are available across Devon, Cornwall, Somerset and Dorset — and remotely nationwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/commercial-epc">
                  <Button className="bg-gradient-to-r from-green-600 to-cyan-500 text-white px-8 py-6 text-lg rounded-full">
                    Commercial EPC services
                  </Button>
                </Link>
                <Link to="/services/commercial_advice">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    MEES Commercial Advice
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-cyan-500">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Have a building you want to model?</h2>
              <p className="text-lg mb-6 opacity-95">Send us the address — we&rsquo;ll come back with a quote in 2 minutes and a realistic improvement plan.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/booking">
                  <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CaseStudies;
